import { Injectable } from '@angular/core';
import { RequestsProvider } from '../requests/requests';

import { StorageProvider } from '../storage/storage';
import { NetworkProvider } from '../network/network';
import { OfflineSyncProvider } from '../offline-sync/offline-sync';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertController } from '@ionic/angular';
import { environment } from '../../../../environments/environment';
import { InventaireLocalisationResponse } from '../../models/inventaire.model';
import { LocalisationControllerApi } from '../../../api/services/localisation-controller-api';
import { firstValueFrom } from 'rxjs';
import { GetLocalisationById$Params } from '../../../api/fn/localisation-controller/get-localisation-by-id';
import { LocalisationDtoEnrichi } from '../../../api/models/localisation-dto-enrichi';
import { GeocodeAdresseIntervention$Params } from '../../../api/fn/geocodage-controller/geocode-adresse-intervention';

export type SiteFileResponse = {
  contenu: string | Blob;
  mime: string;
  id_document: string;
  date_dernier_depot: string;
  nom_dernier_auteur: string;
  nom: string;
};

export interface PostFileRequest {
  metadata: {
    nom: string;
    origine: {
      nom: string;
    };
  };
  contenu: string | Blob;
}

@Injectable({ providedIn: 'root' })
export class LocalisationsProvider {
  apiUrl: string = null;
  siteApiUrl: string = null;
  private logger: LoggerWrapper;

  constructor(
    private translate: TranslateService,
    private requestsProvider: RequestsProvider,
    private storageProvider: StorageProvider,
    private alertCtrl: AlertController,
    private networkProvider: NetworkProvider,
    private offlineSyncProvider: OfflineSyncProvider,
    private loggerFactory: LoggerFactory,
    private localisationApi: LocalisationControllerApi
  ) {
    this.apiUrl = environment.apis.API_DACTECH_BACK_V1;

    this.siteApiUrl = environment.apis.API_SITE;
    this.logger = this.loggerFactory.getInstance(this);
  }

  async searchElargie(search: string): Promise<InventaireLocalisationResponse[]> {
    try {
      let loc;

      if (await this.networkProvider.isReallyConnected()) {
        console.log(this.siteApiUrl + '/recherche_elargie/');
        loc = await (<Promise<any>>this.requestsProvider.get(this.siteApiUrl + '/recherche_elargie/' + search));
      }
      return loc;
    } catch (err) {
      this.logger.warn('error searchElargie', err);
      return [];
    }
  }

  async getLocalisationDtoByIdAvecAdresseGen(idLoc: string): Promise<LocalisationDtoEnrichi> {
    const localisation = await this.getLocalisationDtoById(idLoc);

    if (localisation.adresses?.length > 0) {
      for (let adresse of localisation.adresses) {
        if (adresse.natures_adresse && adresse.natures_adresse.indexOf('GEN') > -1) {
          localisation.adresse = adresse;
        }
      }
    }
    return localisation;
  }

  async getLocalisationDtoById(idLoc: string): Promise<LocalisationDtoEnrichi> {
    try {
      const param: GetLocalisationById$Params = {
        idLocalisation: idLoc,
      };
      return await firstValueFrom(this.localisationApi.getLocalisationById(param));
    } catch (err) {
      this.logger.error(err, 'error getLocalisationDtoById');
      return undefined;
    }
  }

  async getLocalisationById(idLoc: string): Promise<InventaireLocalisationResponse | undefined> {
    try {
      let urlToCall: string = this.apiUrl + '/localisations/' + idLoc;
      return (await this.requestsProvider.getOffline(urlToCall)) as InventaireLocalisationResponse;
    } catch (err) {
      this.logger.warn('error getLocalisationById', err);
      return undefined;
    }
  }

  async getLocalisationsBySite(idSite: string): Promise<InventaireLocalisationResponse[]> {
    try {
      let urlToCall = this.apiUrl + '/localisations/?id_site=' + idSite;
      let locs: InventaireLocalisationResponse[];
      if (await this.networkProvider.isReallyConnected()) {
        locs = await (<Promise<InventaireLocalisationResponse[]>>this.requestsProvider.get(urlToCall));
      } else {
        locs = await this.storageProvider.get(this.storageProvider.getStorageName(urlToCall), true);
      }

      return locs;
    } catch (err) {
      this.logger.warn('error getActivités', err);
      return [];
    }
  }

  getNestedChildren(siteLocArray, idParent) {
    var out = [];
    siteLocArray.forEach((loc) => {
      if (loc.id_localisation_parent === idParent) {
        var children = this.getNestedChildren(siteLocArray, loc.id);

        if (children.length) {
          loc.children = children;
        }
        out.push(loc);
      }
    });

    return out;
  }

  async getArboEquipement(equipementIdLocalisation: string) {
    try {
      let tab_loc = [];
      let arbo_text = '';
      let equipement_localisation = await this.getLocalisationById(equipementIdLocalisation);
      if (!equipement_localisation) return {};
      arbo_text += equipement_localisation.code_localisation;
      tab_loc.push(equipement_localisation);

      while (equipement_localisation.id_localisation_parent) {
        equipement_localisation = await this.getLocalisationById(equipement_localisation.id_localisation_parent);
        arbo_text += ' / ' + equipement_localisation.code_localisation;
        tab_loc.unshift(equipement_localisation);
      }
      return { arbo: tab_loc, arbo_text: arbo_text, nom_long: equipement_localisation.libelle_long };
    } catch (error) {
      this.logger.warn('error get getArboEquipement', error);
      return {};
    }
  }

  async getArboLocalisation(localisation, compact = false) {
    try {
      let parent_site;
      let tab_loc = [];
      if (!compact) {
        parent_site = await this.getLocalisationById(localisation.id_site);
      }
      let string_loc = '';
      let arbo_localisation: any = {};
      let base_loc = localisation;

      if (!base_loc.libelle_long && base_loc.libelle_court) base_loc.libelle_long = base_loc.libelle_court;
      let tab_base_loc = base_loc.libelle_long.split('/');

      base_loc.libelle_long = tab_base_loc.pop();

      while (localisation && localisation.id_localisation_parent) {
        if (!localisation.libelle_long && localisation.libelle_court)
          localisation.libelle_long = localisation.libelle_court;

        let tab_loc_tmp = localisation.libelle_long.split('/');

        localisation.libelle_long = tab_loc_tmp.pop();
        tab_loc.unshift({
          type: localisation.type_localisation,
          libelle: localisation.libelle_long,
        });
        // let old = localisation.id_localisation_parent;
        localisation = await this.getLocalisationById(localisation.id_localisation_parent);
        // console.log("getLocalisationById ", old, localisation)
      }

      if (!compact) {
        tab_loc.unshift({
          type: parent_site.type_localisation,
          libelle: parent_site.libelle_long,
        });
      }

      tab_loc.map((loc) => {
        loc.type = this.capitalizeFirstLetter(loc.type);
        if (!compact) string_loc += `${loc.type} : ${loc.libelle}, `;
        else string_loc += `${loc.type} : ${loc.libelle} / `;
      });
      // console.log("show_base", show_base);
      // console.log("tab_loc", tab_loc);
      // console.log("parent_site", parent_site);
      // console.log("base_loc", base_loc);
      if (compact) {
        if (localisation && localisation.libelle_long) arbo_localisation.libelle_long = localisation.libelle_long;
        else return null;
      } else if (parent_site.libelle_long == base_loc.libelle_long)
        arbo_localisation.libelle_long = parent_site.libelle_long;
      else arbo_localisation.libelle_long = `${parent_site.libelle_long}/${base_loc.libelle_court}`;
      arbo_localisation.libelle_text = string_loc.slice(0, -2);

      return arbo_localisation;
    } catch (error) {
      this.logger.warn('error get ArboLocalisation', error);
      return {};
    }
  }

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  async getFileList(idLoc, type): Promise<SiteFileResponse[]> {
    if (await this.networkProvider.isReallyConnected()) {
      try {
        return await (<Promise<Array<any>>>(
          await this.requestsProvider.get(this.apiUrl + '/localisations/' + idLoc + '/documents/' + type)
        ));
      } catch (ex) {
        this.logger.warn(ex);
        return [];
      }
    } else {
      return [];
    }
  }

  async postFile(idLoc: string, file: PostFileRequest, type: string) {
    const url = this.apiUrl + '/localisations/' + idLoc + '/documents/' + type;

    if (await this.networkProvider.isReallyConnected()) {
      try {
        return await this.requestsProvider.post(url, file);
      } catch (ex) {
        this.logger.error(ex, `Error postFile online`);
        const ERROR_TITLE = 'GLOBAL.ERROR';
        const DOCUMENT_TOO_LARGE = 'GALLERY.DOCUMENT_TOO_LARGE';
        const translations = await this.translate.get([ERROR_TITLE, DOCUMENT_TOO_LARGE]).toPromise();
        this.presentAlert(translations[ERROR_TITLE], translations[DOCUMENT_TOO_LARGE]);
      }
    } else {
      try {
        return await this.offlineSyncProvider.add('POST', url, file, {
          'Content-Type': 'application/json',
        });
      } catch (ex) {
        this.logger.error(ex, `Error postFile offline`);
      }
    }
  }

  /**
   * Affichage d'un message d'erreur en cas d'erreur
   */
  async presentAlert(title, text) {
    let alert = await this.alertCtrl.create({
      header: title,
      subHeader: text,
      buttons: ['OK'],
    });
    await alert.present();
  }

  /**
   * Ajoute le champ à savoir CPC ou Document dans la localisation
   *
   * @param {string} id_loc  id de la localisation
   * @param {any} data objet à envoyer
   */
  async patchLocalisation(id_loc: string, data: any) {
    const url = this.apiUrl + '/localisations/' + id_loc;

    if (await this.networkProvider.isReallyConnected()) {
      try {
        await this.requestsProvider.patch(url, data);
      } catch (ex) {
        this.logger.error(ex, `Error patchLocalisation online`);
      }
    } else {
      try {
        await this.offlineSyncProvider.add('PATCH', url, data, {
          'Content-Type': 'application/json',
        });
      } catch (ex) {
        this.logger.error(ex, `Error patchLocalisation offline`);
      }
    }
  }

  async geocodeLocalisation(
    idLocalisation: string,
    dkCodeLocalisation: string,
    gps_latitude: string,
    gps_longitude: string
  ) {
    let params: GeocodeAdresseIntervention$Params = {
      idLocalisation,
      body: {
        dk_code_localisation: dkCodeLocalisation,
        position_gps: {
          gps_latitude: gps_latitude,
          gps_longitude: gps_longitude,
        },
      },
    };
    return await firstValueFrom(this.localisationApi.geocodeAdresseIntervention(params));
  }
}
